/**
 * What a shared page's visitor can still read back after a link ended with
 * their changes queued (g74). Pure: the store hands in the records it marked
 * unsent and a way to read an item as this device holds it.
 *
 * One line per item, however many records it had: the person wants to know
 * what to type again, not how the queue was shaped. The line names the item
 * as it stands here, which is what they last saw, and one word for what did
 * not reach the server. A deleted item is named from the row before its
 * first queued change: nothing after it arrived, so that is the name the
 * owner still sees, and the one that finds the item.
 *
 * Since g107 an item may have every one of its changes as a record of its
 * own. Two of the readings change with that. An item added and deleted here
 * before anything of it was sent is left out: the owner never had it and the
 * person took it away, so there is nothing to type again. An item that was
 * deleted reads "deleted" whatever else was done to it first. And an item
 * that was only ticked and unticked reads by where it ended: "ticked" if the
 * last tap ticked it, as one folded record would have read.
 */

import type { Item } from "../lib/api.js";
import type { Pending } from "./queue.js";

export type UnsentChange = "added" | "edited" | "ticked" | "deleted";

export interface UnsentLine {
  id: string;
  /** The item's quantity, when it has one, then its text. */
  text: string;
  change: UnsentChange;
}

/**
 * Said in place of the text for the one case with none left: a create that
 * went out once without an answer and was deleted before the link ended.
 */
export const TEXT_NOT_KEPT = "An item whose text was not kept";

/** Which word wins when one item has several records. A create outranks all. */
const RANK: Record<UnsentChange, number> = { added: 3, deleted: 2, edited: 1, ticked: 0 };

function changeOf(record: Pending): UnsentChange {
  if (record.op === "upsert" && record.before === null) return "added";
  if (record.op === "delete") return "deleted";
  if (record.op === "complete") return "ticked";
  if (record.op === "patch") {
    const keys = Object.keys(record.fields);
    if (keys.length === 1 && keys[0] === "done" && record.fields.done === true) return "ticked";
  }
  // An untick, a move, an undone completion and any other edit.
  return "edited";
}

function field(source: unknown, name: "text" | "quantity"): string | null {
  const value = (source as Record<string, unknown> | null | undefined)?.[name];
  return typeof value === "string" && value.trim() ? value : null;
}

export function describeUnsent(records: readonly Pending[], rowOf: (id: string) => Item | undefined): UnsentLine[] {
  const byId = new Map<string, { change: UnsentChange; records: Pending[] }>();
  for (const record of [...records].sort((a, b) => a.seq - b.seq)) {
    if (!record.unsent || record.entity !== "item") continue;
    const change = changeOf(record);
    const seen = byId.get(record.id);
    if (!seen) byId.set(record.id, { change, records: [record] });
    else {
      seen.records.push(record);
      if (RANK[change] > RANK[seen.change]) seen.change = change;
    }
  }
  return [...byId].flatMap(([id, { change: strongest, records: chain }]) => {
    const row = rowOf(id);
    const first = chain[0]!;
    const deleted = chain.some((record) => record.op === "delete");
    // Made and removed on this device, and never on the wire.
    if (deleted && first.op === "upsert" && first.before === null && !first.sent) return [];
    const taps = chain.every((record) => record.op === "patch" && Object.keys(record.fields).length === 1 && "done" in record.fields);
    const change = deleted ? "deleted" : taps ? (chain.at(-1)!.fields.done === true ? "ticked" : "edited") : strongest;
    const text = field(row, "text") ?? field(first.before, "text") ??
      chain.map((r) => field(r.fields, "text")).find(Boolean) ?? null;
    const quantity = row ? field(row, "quantity") : field(first.before, "quantity") ?? field(first.fields, "quantity");
    return [{ id, change, text: text === null ? TEXT_NOT_KEPT : quantity ? `${quantity} ${text}` : text }];
  });
}

/** The lines as plain text, one per item, for the clipboard. */
export function unsentText(lines: readonly UnsentLine[]): string {
  return lines.map((line) => `${line.text} (${line.change})`).join("\n");
}
